import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store from '../state';
import maplibregl from 'maplibre-gl';

export class WildfireLayer extends BaseProduct {
  public readonly id: ProductId = 'wildfires';
  private popup: maplibregl.Popup | null = null;
  private mapEventsBound: boolean = false;

  private validAsOfTimestamp: string | null = null;

  protected onActivate(): void {
    this.refresh();
    this.bindMapEvents();
  }

  protected onDeactivate(): void {
    this.removeWildfireLayers();
    this.unbindMapEvents();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh() {
    if (!this.active) return;
    this.setupWildfireLayers();
    this.updateWildfireVisibility();
  }

  private setupWildfireLayers() {
    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = "https://auth.skyskout.com/";
    } else if (apiServer === "") {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const dataUrl = `${apiServer}${tileRoot}/observed/wildfires/wildfires.geojson`;

    // Fetch root metadata (valid_as_of timestamp)
    fetch(dataUrl)
      .then(async res => {
        if (!res.ok) return;
        const text = await res.text();
        // Parse metadata properties near start of JSON string if present
        const match = text.match(/"valid_as_of"\s*:\s*"([^"]+)"/);
        if (match) {
          this.validAsOfTimestamp = match[1];
        }
      })
      .catch(err => {
        console.warn('⚠️ Could not fetch wildfire valid_as_of metadata:', err);
      });

    // Add Wildfires GeoJSON Source pointing to Worker R2 Gateway
    if (!this.map.getSource('wildfires-source')) {
      this.map.addSource('wildfires-source', {
        type: 'geojson',
        data: dataUrl
      });
    } else {
      const source = this.map.getSource('wildfires-source') as maplibregl.GeoJSONSource;
      if (source && typeof source.setData === 'function') {
        source.setData(dataUrl);
      }
    }

    // Dynamic layer ordering: insert dynamic overlays beneath city labels but above background fill layers
    const layers = this.map.getStyle()?.layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    // 1. Fill Layer: Semi-transparent red/orange interior
    if (!this.map.getLayer('wildfires-perimeter-fill')) {
      this.map.addLayer({
        id: 'wildfires-perimeter-fill',
        type: 'fill',
        source: 'wildfires-source',
        paint: {
          'fill-color': '#ef4444',
          'fill-opacity': 0.35
        }
      }, beforeId);
    }

    // 3. Stroke Layers
    if (!this.map.getLayer('wildfires-perimeter-stroke-bg')) {
      this.map.addLayer({
        id: 'wildfires-perimeter-stroke-bg',
        type: 'line',
        source: 'wildfires-source',
        paint: {
          'line-color': '#ffffff',
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 3.5, 8, 5.5, 14, 8.5]
        }
      }, beforeId);
    }

    if (!this.map.getLayer('wildfires-perimeter-stroke-fg')) {
      this.map.addLayer({
        id: 'wildfires-perimeter-stroke-fg',
        type: 'line',
        source: 'wildfires-source',
        paint: {
          'line-color': '#f97316',
          'line-width': ['interpolate', ['linear'], ['zoom'], 3, 2.0, 8, 3.5, 14, 6.0],
          'line-dasharray': [3, 2]
        }
      }, beforeId);
    }

    // 4. Symbol Layer: Fire name centered
    if (!this.map.getLayer('wildfires-perimeter-label')) {
      this.map.addLayer({
        id: 'wildfires-perimeter-label',
        type: 'symbol',
        source: 'wildfires-source',
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 3, 11, 8, 13, 14, 16],
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 2.0
        }
      }, beforeId);
    }

    // 5. Thermal Hotspots Vector Circle Layer
    if (!this.map.getLayer('wildfires-hotspots-circle')) {
      this.map.addLayer({
        id: 'wildfires-hotspots-circle',
        type: 'circle',
        source: 'wildfires-source',
        filter: ['==', ['get', 'incident_type'], 'HOTSPOT'],
        paint: {
          'circle-color': [
            'interpolate', ['linear'], ['coalesce', ['get', 'frp'], 0],
            0, '#ef4444',
            20, '#f97316',
            100, '#fbbf24'
          ],
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            3, 2.5,
            7, 4.5,
            12, 7.5
          ],
          'circle-opacity': 0.85,
          'circle-stroke-width': 1.0,
          'circle-stroke-color': '#ffffff'
        }
      }, beforeId);
    }
  }

  private updateWildfireVisibility() {
    const state = store.getState();
    const isPerimetersEnabled = (state.wildfiresEnabled !== false);
    const isCanadaEnabled = (state.wildfiresCanadaEnabled !== false);
    const isPrescribedEnabled = (state.prescribedBurnsEnabled === true);
    const isAnyPerimeterEnabled = isPerimetersEnabled || isCanadaEnabled || isPrescribedEnabled;

    const types = [];
    if (isPerimetersEnabled) types.push('WF');
    if (isCanadaEnabled) types.push('WF_CA');
    if (isPrescribedEnabled) types.push('RX');

    let filterExpr: any;
    if (types.length === 0) {
      filterExpr = ['literal', false];
    } else if (types.length === 1) {
      filterExpr = ['==', ['get', 'incident_type'], types[0]];
    } else {
      filterExpr = ['in', ['get', 'incident_type'], ['literal', types]];
    }

    const perimeterLayers = [
      'wildfires-perimeter-fill',
      'wildfires-perimeter-stroke-bg',
      'wildfires-perimeter-stroke-fg',
      'wildfires-perimeter-label'
    ];

    perimeterLayers.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', isAnyPerimeterEnabled ? 'visible' : 'none');
        this.map.setFilter(layerId, filterExpr);
      }
    });

    const isHotspotsEnabled = (state.wildfireHotspotsEnabled !== false);
    if (this.map.getLayer('wildfires-hotspots-circle')) {
      this.map.setLayoutProperty('wildfires-hotspots-circle', 'visibility', isHotspotsEnabled ? 'visible' : 'none');
    }
  }

  private removeWildfireLayers() {
    const layers = [
      'wildfires-hotspots-circle',
      'wildfires-perimeter-label',
      'wildfires-perimeter-stroke-fg',
      'wildfires-perimeter-stroke-bg',
      'wildfires-perimeter-fill'
    ];
    layers.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });

    const sources = ['wildfires-source'];
    sources.forEach(sourceId => {
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    });
  }

  private bindMapEvents() {
    if (this.mapEventsBound) return;
    this.mapEventsBound = true;

    this.popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'gw-wildfire-popup'
    });

    const targetLayers = [
      'wildfires-perimeter-fill',
      'wildfires-hotspots-circle'
    ];

    targetLayers.forEach(layerId => {
      this.map.on('mouseenter', layerId, this.handleMouseEnter);
      this.map.on('mouseleave', layerId, this.handleMouseLeave);
      this.map.on('click', layerId, this.handleClick);
    });
  }

  private unbindMapEvents() {
    if (!this.mapEventsBound) return;
    this.mapEventsBound = false;

    const targetLayers = [
      'wildfires-perimeter-fill',
      'wildfires-hotspots-circle'
    ];

    targetLayers.forEach(layerId => {
      this.map.off('mouseenter', layerId, this.handleMouseEnter);
      this.map.off('mouseleave', layerId, this.handleMouseLeave);
      this.map.off('click', layerId, this.handleClick);
    });

    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
  }

  private handleMouseEnter = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
    this.map.getCanvas().style.cursor = 'default';
    if (!e.features || e.features.length === 0 || !this.popup) return;

    const feature = e.features[0];
    const props = feature.properties || {};

    const formatTimestamp = (raw: any): string | null => {
      if (!raw) return null;
      try {
        const d = new Date(raw);
        if (!isNaN(d.getTime())) {
          return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
        }
      } catch {
        // fallback
      }
      return String(raw);
    };

    let updateTimeStr = '';
    if (this.validAsOfTimestamp) {
      try {
        const d = new Date(this.validAsOfTimestamp);
        updateTimeStr = d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
      } catch (err) {
        updateTimeStr = this.validAsOfTimestamp;
      }
    }

    let content = '';
    if (props.incident_type === 'HOTSPOT') {
      const frp = props.frp !== undefined ? `${props.frp} MW` : 'N/A';
      const sat = props.satellite || 'VIIRS';
      const rawDetect = props.acq_datetime || props.updated_date;
      const detectStr = formatTimestamp(rawDetect) || rawDetect || '';
      content = `
        <div style="background: #0f172a; color: #f8fafc; border: 1px solid #f97316; border-radius: 8px; font-family: inherit; font-size: 12px; line-height: 1.5; padding: 10px 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.6);">
          <strong style="color: #f97316; font-size: 14px; display: block; margin-bottom: 4px;">🔥 Thermal Hotspot (${frp})</strong>
          <div><span style="color: #94a3b8;">Satellite:</span> <strong>${sat}</strong></div>
          ${detectStr ? `<div><span style="color: #94a3b8;">Detected:</span> <strong>${detectStr}</strong></div>` : ''}
          ${updateTimeStr ? `<div style="font-size: 10.5px; color: #64748b; margin-top: 4px; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 3px;">Feed Synced: ${updateTimeStr}</div>` : ''}
        </div>
      `;
    } else {
      const name = props.name || 'Wildfire Perimeter';
      const acres = props.acres ? Number(props.acres).toLocaleString() + ' acres' : 'Size unknown';
      const containment = (props.containment !== undefined && props.containment !== null && props.containment !== '') 
        ? (typeof props.containment === 'number' ? `${props.containment}% contained` : String(props.containment))
        : 'Unknown containment';
      const startDate = formatTimestamp(props.start_date);
      const rawUpdated = props.updated_date || props.report_date || props.poly_PolygonDateTime || props.last_updated;
      const updatedDate = formatTimestamp(rawUpdated);

      content = `
        <div style="background: #0f172a; color: #f8fafc; border: 1px solid #ef4444; border-radius: 8px; font-family: inherit; font-size: 12px; line-height: 1.5; padding: 10px 12px; min-width: 180px; box-shadow: 0 4px 16px rgba(0,0,0,0.6);">
          <strong style="color: #ef4444; font-size: 14px; display: block; margin-bottom: 4px;">🔥 ${name}</strong>
          <div><span style="color: #94a3b8;">Size:</span> <strong>${acres}</strong></div>
          <div><span style="color: #94a3b8;">Containment:</span> <strong>${containment}</strong></div>
          ${startDate ? `<div><span style="color: #94a3b8;">Start Date:</span> <strong>${startDate}</strong></div>` : ''}
          ${updatedDate ? `<div><span style="color: #94a3b8;">Perimeter Updated:</span> <strong>${updatedDate}</strong></div>` : ''}
          ${updateTimeStr ? `<div style="font-size: 10.5px; color: #64748b; margin-top: 4px; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 3px;">Feed Synced: ${updateTimeStr}</div>` : ''}
        </div>
      `;
    }

    this.popup.setLngLat(e.lngLat).setHTML(content).addTo(this.map);
  };

  private handleMouseLeave = () => {
    this.map.getCanvas().style.cursor = '';
    if (this.popup) {
      this.popup.remove();
    }
  };

  private handleClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
    // Suppress click interactions on wildfire perimeters and hotspots (do not open POI forecast page)
    if (e.originalEvent) {
      e.originalEvent.stopPropagation();
      e.originalEvent.preventDefault();
    }
  };
}

export default WildfireLayer;
